/**
 * @fileoverview Session API Contract (Contract-First Design)
 * @module types/contracts
 *
 * Complete API contract for the Session module.
 * Single source of truth for all session-related API definitions.
 */

import { z } from 'zod';
import { createContract, extractRoutes, ApiSuccessResponseSchema } from './contract.utils';

// ============================================================================
// ENUMS
// ============================================================================

/**
 * Session Status Enum
 */
export enum SessionStatus {
	ACTIVE = 'active',
	CONVERTED = 'converted',
	EXPIRED = 'expired',
	REVOKED = 'revoked',
}

/**
 * Client Type Enum
 */
export enum ClientType {
	WEB = 'web',
	MOBILE = 'mobile',
	ADMIN = 'admin',
}

// ============================================================================
// SCHEMAS (Data Structure & Validation)
// ============================================================================

/**
 * Session ID Parameter Schema
 */
export const SessionIdParamSchema = z.object({
	id: z.uuid('Invalid session ID format'),
});
export type SessionIdParam = z.infer<typeof SessionIdParamSchema>;

/**
 * Create Session Request Schema
 */
export const CreateSessionSchema = z.object({
	clientType: z.enum(ClientType).default(ClientType.WEB),
	fingerprint: z.string().optional(),
	metadata: z.record(z.string(), z.unknown()).optional(),
});
export type CreateSessionPayload = z.infer<typeof CreateSessionSchema>;

/**
 * Promote Session Request Schema
 * Used when a visitor becomes a user (e.g., after purchase)
 */
export const PromoteSessionSchema = z.object({
	userId: z.uuid('Invalid user ID format'),
	metadata: z.record(z.string(), z.unknown()).optional(),
});
export type PromoteSessionPayload = z.infer<typeof PromoteSessionSchema>;

/**
 * Session Metadata Schema
 */
export const SessionMetadataSchema = z
	.object({
		utmSource: z.string().optional(),
		utmMedium: z.string().optional(),
		utmCampaign: z.string().optional(),
		adClickId: z.string().optional(), // GCLID/FBCLID
		referrer: z.string().optional(),
		landingPage: z.string().optional(),
		analysisType: z.string().optional(),
		paymentIntentId: z.string().optional(),
		purchaseAmount: z.number().optional(),
		conversionDate: z.string().optional(),
		revenue: z.number().optional(),
	})
	.passthrough(); // Allow additional fields
export type SessionMetadata = z.infer<typeof SessionMetadataSchema>;

/**
 * Session Tracking Entity Schema (API Response)
 *
 * NOTE: csrfToken is NOT stored in database - it only exists in Express Session (Redis)
 * Use /session/verify endpoint to get the CSRF token from the session
 */
export const SessionTrackingSchema = z.object({
	id: z.uuid(),
	sessionId: z.string(),
	userId: z.uuid().nullable(),
	clientType: z.enum(ClientType),
	status: z.enum(SessionStatus),
	fingerprint: z.string(),
	ipAddress: z.string().nullable(),
	userAgent: z.string().nullable(),
	isBot: z.boolean(),
	metadata: SessionMetadataSchema.nullable(),
	lastActivityAt: z.iso.datetime(),
	expiresAt: z.iso.datetime(),
	createdAt: z.iso.datetime(),
	updatedAt: z.iso.datetime(),
});
export type SessionTracking = z.infer<typeof SessionTrackingSchema>;

/**
 * Initialize Session Response Schema
 */
export const InitializeSessionResponseSchema = z.object({
	sessionId: z.string(),
	csrfToken: z.string(),
});
export type InitializeSessionResponse = z.infer<typeof InitializeSessionResponseSchema>;

/**
 * Verify Session Response Schema
 *
 * NOTE: userId can be null (not logged in)
 * csrfToken is always a string (empty string if not yet initialized)
 */
export const VerifySessionResponseSchema = z.object({
	sessionId: z.string(),
	userId: z.uuid().nullable(),
	csrfToken: z.string(),
});

export const VerifySessionApiResponseSchema = ApiSuccessResponseSchema(VerifySessionResponseSchema);
export type VerifySessionApiResponse = z.infer<typeof VerifySessionApiResponseSchema>;

/**
 * Delete Session Response Schema
 */
export const DeleteSessionResponseSchema = z.object({
	message: z.string(),
});
export type DeleteSessionResponse = z.infer<typeof DeleteSessionResponseSchema>;

/**
 * Cleanup Sessions Response Schema
 */
export const CleanupSessionsResponseSchema = z.object({
	marked: z.number().int().nonnegative(),
});
export type CleanupSessionsResponse = z.infer<typeof CleanupSessionsResponseSchema>;

/**
 * Promote Session Response Schema
 */
export const PromoteSessionResponseSchema = z.object({
	message: z.string(),
	session: SessionTrackingSchema,
});
export type PromoteSessionResponse = z.infer<typeof PromoteSessionResponseSchema>;

/**
 * Clear Payment Flag Response Schema
 */
export const ClearPaymentFlagResponseSchema = z.object({
	message: z.string(),
});
export type ClearPaymentFlagResponse = z.infer<typeof ClearPaymentFlagResponseSchema>;

// ============================================================================
// API RESPONSE SCHEMAS (Wrapped with success, data, timestamp)
// ============================================================================

/**
 * Initialize Session API Response
 * { success: true, data: InitializeSessionResponse, timestamp: string }
 */
export const InitializeSessionApiResponseSchema = ApiSuccessResponseSchema(
	InitializeSessionResponseSchema,
);



/**
 * Delete Session API Response
 * { success: true, data: DeleteSessionResponse, timestamp: string }
 */
export const DeleteSessionApiResponseSchema = ApiSuccessResponseSchema(
	DeleteSessionResponseSchema,
);

/**
 * Cleanup Sessions API Response
 * { success: true, data: CleanupSessionsResponse, timestamp: string }
 */
export const CleanupSessionsApiResponseSchema = ApiSuccessResponseSchema(
	CleanupSessionsResponseSchema,
);

/**
 * Promote Session API Response
 * { success: true, data: PromoteSessionResponse, timestamp: string }
 */
export const PromoteSessionApiResponseSchema = ApiSuccessResponseSchema(
	PromoteSessionResponseSchema,
);

/**
 * Session Tracking API Response
 * { success: true, data: SessionTracking, timestamp: string }
 */
export const SessionTrackingApiResponseSchema = ApiSuccessResponseSchema(SessionTrackingSchema);

/**
 * Clear Payment Flag API Response
 * { success: true, data: ClearPaymentFlagResponse, timestamp: string }
 */
export const ClearPaymentFlagApiResponseSchema = ApiSuccessResponseSchema(
	ClearPaymentFlagResponseSchema,
);
export type ClearPaymentFlagApiResponse = z.infer<typeof ClearPaymentFlagApiResponseSchema>;

// ============================================================================
// API CONTRACT (Routes, Methods, Request/Response)
// ============================================================================

/**
 * Session API Contract
 *
 * Defines the complete API surface for the Session module.
 * Both frontend and backend use this as the single source of truth.
 */
export const SESSION_API_CONTRACT = createContract('/session', {
	/**
	 * POST /session
	 * Initialize a new session
	 */
	initialize: {
		method: 'POST' as const,
		path: '',
		body: CreateSessionSchema,
		response: InitializeSessionResponseSchema,
	},

	/**
	 * GET /session/verify
	 * Verify current session
	 */
	verify: {
		method: 'GET' as const,
		path: '/verify',
		response: VerifySessionApiResponseSchema,
	},

	/**
	 * DELETE /session
	 * Delete current session (logout)
	 */
	delete: {
		method: 'DELETE' as const,
		path: '',
		response: DeleteSessionResponseSchema,
	},

	/**
	 * POST /session/cleanup
	 * Mark expired sessions in logs (admin endpoint)
	 */
	cleanup: {
		method: 'POST' as const,
		path: '/cleanup',
		response: CleanupSessionsResponseSchema,
	},

	/**
	 * POST /session/promote
	 * Promote anonymous session to user session (internal use)
	 */
	promote: {
		method: 'POST' as const,
		path: '/promote',
		body: PromoteSessionSchema,
		response: PromoteSessionResponseSchema,
	},

	/**
	 * GET /session/:id
	 * Get session by ID (admin endpoint)
	 */
	getById: {
		method: 'GET' as const,
		path: '/:id',
		params: SessionIdParamSchema,
		response: SessionTrackingSchema,
	},

	/**
	 * POST /session/clear-payment-flag
	 * Clear payment completion flag from session (after thank-you page viewed)
	 */
	clearPaymentFlag: {
		method: 'POST' as const,
		path: '/clear-payment-flag',
		response: ClearPaymentFlagApiResponseSchema,
	},
});

/**
 * Route Constants for Controllers
 * Use these in @Controller() and @Get/@Post/@Delete decorators
 */
export const SESSION_ROUTES = extractRoutes(SESSION_API_CONTRACT);

// ============================================================================
// TYPE EXPORTS
// ============================================================================

/**
 * Export all types for use in frontend and backend
 */
export type SessionApiContract = typeof SESSION_API_CONTRACT;
